import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { ItineraryCard } from "@/lib/itineraries/ItineraryCard";
import { ItinerariesTable } from "@/lib/itineraries/ItinerariesTable";
import { TravelDesignerFilter } from "@/lib/itineraries/TravelDesignerFilter";
import { ClientFilter } from "@/lib/itineraries/ClientFilter";
import { ViewTravelRequestButton, type ViewMode } from "@/lib/requests";
import { useI18n } from "@/hooks/useI18n";
import { DashboardItinerary } from "@/lib/models/dashboard";
import { ItineraryTabProps } from "./types";

export const DashboardItinerariesTab = ({
  config,
  userRole,
  travelRequests,
  loading,
  error,
  onEdit,
  onDelete,
  onDuplicate,
  onViewTravelRequest,
  onUpdateStatus,
  onRefresh,
  refreshing,
}: ItineraryTabProps) => {
  const { t } = useI18n();
  const { data, showDesignerFilter, i18n } = config;

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDesigner, setSelectedDesigner] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<string | null>(null);

  // Reset filters when source data changes
  useEffect(() => {
    setSelectedDesigner(null);
    setSelectedClient(null);
  }, [data]);

  // Compose both filters
  const filteredData = useMemo(() => {
    let result = data;
    if (selectedDesigner) {
      result = result.filter(
        (it) => it.assignedAdvisor?.fullName === selectedDesigner ||
                `${it.assignedAdvisor?.firstName ?? ''} ${it.assignedAdvisor?.lastName ?? ''}`.trim() === selectedDesigner
      );
    }
    if (selectedClient) {
      result = result.filter((it) => it.contactEmail === selectedClient);
    }
    return result;
  }, [data, selectedDesigner, selectedClient]);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    if (mode === 'grid') setCurrentPage(1);
  };

  const countLabel = data.length
    ? t(i18n.countKey as any, { count: data.length })
    : t(i18n.noCountKey as any);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-4 sm:space-y-0 sm:flex sm:justify-between sm:items-center">
        <div className="flex items-center gap-2">
          <h6 className="text-gray-500">{countLabel}</h6>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {(userRole === "admin" || userRole === "agency") && showDesignerFilter && (
            <TravelDesignerFilter
              selectedDesigner={selectedDesigner}
              onDesignerChange={setSelectedDesigner}
              itineraries={data}
              onFilteredItinerariesChange={() => {}}
              disabled={data.length === 0}
            />
          )}
          {(userRole === "admin" || userRole === "agency" || userRole === "travel_designer" || userRole === "travel_advisor_member") && showDesignerFilter && (
            <ClientFilter
              selectedClient={selectedClient}
              onClientChange={setSelectedClient}
              itineraries={data}
              disabled={data.length === 0}
            />
          )}
          <ViewTravelRequestButton
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            disabled={data.length === 0}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">{t('dashboard.refresh')}</span>
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="relative">
        {filteredData.length > 0 ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredData.map((itinerary, index) => (
                <div
                  key={itinerary.id}
                  className="animate-in fade-in zoom-in-95 duration-500"
                  style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'backwards' }}
                >
                  <ItineraryCard
                    itinerary={itinerary}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onDuplicate={onDuplicate}
                    onViewTravelRequest={onViewTravelRequest}
                    onViewItinerary={onEdit}
                    onUpdateStatus={onUpdateStatus}
                    onShared={onRefresh}
                    travelRequests={travelRequests}
                    isAdmin={userRole === "admin" || userRole === "agency"}
                    isTraveler={userRole === "traveler"}
                    userRole={userRole}
                  />
                </div>
              ))}
            </div>
          ) : (
            <ItinerariesTable
              itineraries={filteredData}
              currentPage={currentPage}
              itemsPerPage={10}
              onPageChange={setCurrentPage}
              onEdit={onEdit}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onViewTravelRequest={onViewTravelRequest}
              onViewItinerary={onEdit}
              onUpdateStatus={onUpdateStatus}
              onShared={onRefresh}
              isAdmin={userRole === "admin" || userRole === "agency"}
              isTraveler={userRole === "traveler"}
              userRole={userRole}
            />
          )
        ) : (
          <div className="text-center py-12">
            <h4 className="text-gray-600 mb-4">
              {selectedDesigner && i18n.emptyTitleDesignerKey
                ? t(i18n.emptyTitleDesignerKey as any, { designer: selectedDesigner })
                : t(i18n.emptyTitleKey as any)}
            </h4>
            {(i18n.emptyDescKey || i18n.emptyDescDesignerKey) && (
              <p className="text-gray-500 mb-4">
                {selectedDesigner && i18n.emptyDescDesignerKey
                  ? t(i18n.emptyDescDesignerKey as any)
                  : i18n.emptyDescKey
                    ? t(i18n.emptyDescKey as any)
                    : null}
              </p>
            )}
            {selectedDesigner && (
              <Button variant="outline" onClick={() => setSelectedDesigner(null)} className="mt-2">
                {t('dashboard.clearFilter')}
              </Button>
            )}
            {selectedClient && (
              <Button variant="outline" onClick={() => setSelectedClient(null)} className="mt-2">
                {t('dashboard.clearFilter')}
              </Button>
            )}
          </div>
        )}

        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex justify-center items-center z-10 rounded-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="text-center py-12">
          <h4 className="text-red-600">{t(i18n.errorKey as any)}</h4>
          <Button onClick={onRefresh} className="mt-4">
            {t('dashboard.retry')}
          </Button>
        </div>
      )}
    </div>
  );
};
